/**
 * useReviewDetailState — data + composer state for one flagged item (BON-381).
 *
 * Resolve and reopen are the SAME transaction shape: a note, stamped with who
 * and when. So one composer serves both, and one submit path validates both —
 * a blank note is refused here, before the round trip, matching the API's 400.
 * A successful transition swaps in the `ReviewDetail` the write returned, so
 * the page reflects the new state (and the new history entry) without a refetch.
 * A 409 is the opposite case — someone else moved it first — and is answered by
 * refetching, because the only thing wrong is our copy.
 */
import { useCallback, useEffect, useState } from 'react';

import { fetchReviewDetail, reopenReview, resolveReview } from './reviewApi';
import { ReviewApiError } from './reviewApiError';
import { markReviewCountStale } from './reviewCountSignal';
import { noteError } from './reviewQueueModel';
import type { ReviewDetail, ReviewKind } from './reviewTypes';

export type ComposerMode = 'resolve' | 'reopen';

/** The load, guarded against a stale response when `:kind`/`:id` change. */
function useDetailFetch(kind: ReviewKind, id: string) {
  const [detail, setDetail] = useState<ReviewDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Bumped to re-run the load. Needed because a 409 means our copy is stale, and
  // the only honest answer to that is to go and get the current one.
  const [reloadNonce, setReloadNonce] = useState(0);
  const reload = useCallback(() => setReloadNonce((n) => n + 1), []);

  useEffect(() => {
    // A malformed route (bad `:kind`, missing `:id`) renders its own state; do
    // not spend a request the API would only 400.
    if (!id) {
      setLoading(false);
      return;
    }
    const controller = new AbortController();
    const { signal } = controller;
    setLoading(true);
    setError(null);
    setDetail(null);
    fetchReviewDetail(kind, id, signal)
      .then((res) => {
        if (!signal.aborted) setDetail(res);
      })
      .catch((err: unknown) => {
        if (signal.aborted || (err instanceof DOMException && err.name === 'AbortError')) return;
        setError(err instanceof Error ? err.message : 'Failed to load this flagged item');
      })
      .finally(() => {
        if (!signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [kind, id, reloadNonce]);

  return { detail, setDetail, loading, error, reload };
}

export function useReviewDetailState(kind: ReviewKind, id: string) {
  const { detail, setDetail, loading, error, reload } = useDetailFetch(kind, id);

  const [composer, setComposer] = useState<ComposerMode | null>(null);
  const [note, setNote] = useState('');
  const [noteMessage, setNoteMessage] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  /** Read out by a polite live region after a completed transition. */
  const [announcement, setAnnouncement] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const openComposer = useCallback((mode: ComposerMode) => {
    setComposer(mode);
    setNote('');
    setNoteMessage(null);
    setActionError(null);
  }, []);

  const closeComposer = useCallback(() => {
    setComposer(null);
    setNoteMessage(null);
    setActionError(null);
  }, []);

  const changeNote = useCallback((next: string) => {
    setNote(next);
    setNoteMessage(null);
  }, []);

  const submit = useCallback(() => {
    if (!composer || submitting) return;
    const invalid = noteError(note);
    if (invalid) {
      setNoteMessage(invalid);
      return;
    }
    setSubmitting(true);
    setActionError(null);
    setAnnouncement(null);
    const write = composer === 'resolve' ? resolveReview : reopenReview;
    write(kind, id, note)
      .then((next) => {
        setDetail(next);
        setComposer(null);
        setNote('');
        // U1: a successful resolve changed a pill, swapped a button and added a
        // history row, and announced NONE of it — a screen-reader user pressed
        // Confirm and heard silence, with no way to tell whether it had worked.
        // The live region below is the only thing that says so.
        setAnnouncement(
          composer === 'resolve' ? 'Item marked resolved.' : 'Item reopened for review.',
        );
        // U14: the rail badge fetched its count once, in the chrome above this
        // page, so resolving the last flagged item left it advertising work that
        // no longer existed. Announced only after the write COMMITS — the badge
        // must show what the server thinks, not what we hoped.
        markReviewCountStale();
      })
      .catch((err: unknown) => {
        setActionError(err instanceof Error ? err.message : 'That change did not go through');
        // A 409 says someone else already moved this item, so our `detail` is
        // stale — and leaving it on screen kept the page offering "Mark resolved"
        // for an item that is already resolved, which invites the operator to
        // press it again and get the same refusal. Close the composer (the action
        // it was composing no longer applies) and refetch, so the message and the
        // page agree. The old copy said "Reload to see its current state" and was
        // unreachable anyway, because the server's own message always won.
        if (err instanceof ReviewApiError && err.status === 409) {
          setComposer(null);
          setNote('');
          reload();
        }
      })
      .finally(() => setSubmitting(false));
  }, [composer, submitting, note, kind, id, setDetail, reload]);

  return {
    detail,
    loading,
    error,
    composer,
    openComposer,
    closeComposer,
    note,
    changeNote,
    noteMessage,
    actionError,
    announcement,
    submitting,
    submit,
  };
}
